/**
 * textSafeColor.js
 *
 * WCAG-contrast text color for a RAW band hue. A matched coloring-schema value whose match carries
 * a raw `color` (band hexes with no semantic tone) colors the KPI value text itself; this helper
 * derives a legible variant of that hue — same hue and saturation, lightness adjusted until it
 * clears AA 4.5:1 against the widget card surface (COLORING_SCHEMA_RESEARCH.md §4.9). Tone matches
 * never use this (they carry a verified tone class). Deterministic and memoized per (hex, theme).
 *
 * Card surfaces (from CustomDashboard.vue): light = #ffffff (as specified; the real elevated surface
 * #fcfdfc is marginally darker, so #ffffff is the conservative worst case for darkening); dark =
 * #2d2d2d (--ds-surface-elevated, confirmed at CustomDashboard.vue:3043, where the shipped KPI tone
 * overrides target the same surface). Pure leaf module — no store, no component imports.
 */

const CARD_SURFACE = Object.freeze({ light: '#ffffff', dark: '#2d2d2d' });
const MIN_CONTRAST = 4.5; // WCAG AA for normal-size text

// '#rgb' or '#rrggbb' → { r, g, b } (0..255), or null when unparseable.
function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

// { r, g, b } (rounded + clamped to 0..255) → '#rrggbb'.
function rgbToHex({ r, g, b }) {
  const to2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

// WCAG relative luminance of an sRGB color (0..1).
function relativeLuminance({ r, g, b }) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// WCAG contrast ratio between two sRGB colors (1..21).
function contrast(rgb1, rgb2) {
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  const hi = Math.max(l1, l2);
  const lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

// { r, g, b } (0..255) → { h (0..360), s (0..1), l (0..1) }.
function rgbToHsl({ r, g, b }) {
  const rn = r / 255; const gn = g / 255; const bn = b / 255;
  const max = Math.max(rn, gn, bn); const min = Math.min(rn, gn, bn);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0; let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

// { h (0..360), s (0..1), l (0..1) } → { r, g, b } (0..255).
function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0; let g1 = 0; let b1 = 0;
  if (hp < 1) { r1 = c; g1 = x; } else if (hp < 2) { r1 = x; g1 = c; } else if (hp < 3) { g1 = c; b1 = x; } else if (hp < 4) { g1 = x; b1 = c; } else if (hp < 5) { r1 = x; b1 = c; } else { r1 = c; b1 = x; }
  const m = l - c / 2;
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

/**
 * WCAG contrast ratio (1..21) between two hex colors, or null if either is unparseable. Exported
 * for verification.
 * @param {string} hexA
 * @param {string} hexB
 * @returns {number|null}
 */
export function contrastRatio(hexA, hexB) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return null;
  return contrast(a, b);
}

const cache = new Map();

/**
 * A text-safe variant of `hex` for a KPI value on the theme's widget card surface: same hue and
 * saturation, lightness stepped toward the contrast-increasing direction (darker on the light
 * surface, lighter on the dark) until it clears 4.5:1. An already-legible hex is returned unchanged
 * (normalized). Luminance is monotonic in HSL lightness for a fixed hue/saturation, so the first
 * step that clears the threshold is the variant closest to the original. Deterministic + memoized.
 * @param {string} hex   raw '#rrggbb' band color
 * @param {'light'|'dark'} theme
 * @returns {string} a '#rrggbb' hex meeting >= 4.5:1 on the theme surface
 */
export function textSafeColor(hex, theme) {
  const surfaceKey = theme === 'dark' ? 'dark' : 'light';
  const key = `${String(hex).toLowerCase()}::${surfaceKey}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const surface = hexToRgb(CARD_SURFACE[surfaceKey]);
  const rgb = hexToRgb(hex);
  let result;
  if (!rgb) {
    result = surfaceKey === 'dark' ? '#ffffff' : '#000000'; // unparseable → max-contrast fallback
  } else if (contrast(rgb, surface) >= MIN_CONTRAST) {
    result = rgbToHex(rgb); // already legible on this surface
  } else {
    const { h, s, l } = rgbToHsl(rgb);
    const dir = surfaceKey === 'dark' ? 1 : -1; // dark surface → lighten; light surface → darken
    let li = Math.round(l * 100);
    result = null;
    for (let i = 0; i < 100; i += 1) {
      li += dir;
      if (li < 0 || li > 100) break;
      const candidate = hslToRgb(h, s, li / 100);
      if (contrast(candidate, surface) >= MIN_CONTRAST) { result = rgbToHex(candidate); break; }
    }
    // Bound reached (pure black on light, pure white on dark) — both are guaranteed >= 4.5:1.
    if (result === null) result = surfaceKey === 'dark' ? '#ffffff' : '#000000';
  }
  cache.set(key, result);
  return result;
}

/** Drop the memo (tests, or a surface-constant change). */
export function clearTextSafeCache() {
  cache.clear();
}
